import { Fieldset, Label, TextInput } from "nhsuk-react-components"
import { useContext, useState } from "react"
import { REQUIRED_ERROR_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { getResponseForQuestion, getStringValueFromResponse } from "../../fhirUtils/questionnaireUtils"
import type { QuestionProps } from "./Question"
import { questionClass } from "./questionClass"
import { QuestionnaireError } from "./QuestionnaireError"
import { showQuestion } from "./showQuestion"

export const StringQuestion = ({ question, saveResponses, topLevel }: QuestionProps) => {
  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("StringQuestion must be used within a QuestionnaireProvider")
  }
  const { responses, errors } = questionnaireContext

  const [answer, setAnswer] = useState<string>(getStringValueFromResponse(getResponseForQuestion(question, responses)))
  const updateAnswers = (updatedAnswer: string): void => {
    setAnswer(updatedAnswer)

    const valueString = updatedAnswer.trim()
    if (valueString.length > 0) {
      saveResponses({
        linkId: question.linkId,
        text: question.text,
        answer: [
          {
            valueString,
          },
        ],
      })
    } else {
      saveResponses({
        linkId: question.linkId,
        text: question.text,
      })
    }
  }

  if (!showQuestion(question, responses)) {
    return null
  }

  const showRequiredError = !!errors[question.linkId]?.required

  const requiredErrorMessage = showRequiredError
    ? (question.extension?.find((ext) => ext.url === REQUIRED_ERROR_DEFINITION_URL)?.valueMarkdown ??
      "This field is required.")
    : null

  return (
    <>
      {showRequiredError && <QuestionnaireError errorMessage={requiredErrorMessage} />}
      <Fieldset className={questionClass(question, responses)} aria-live="assertive">
        {topLevel ? <Fieldset.Legend isPageHeading>{question.text}</Fieldset.Legend> : <Label>{question.text}</Label>}
        <TextInput
          aria-invalid={showRequiredError}
          error={showRequiredError}
          id={question.linkId}
          name={question.linkId}
          value={answer}
          onChange={(event: React.FormEvent<HTMLInputElement>) => {
            updateAnswers(event.currentTarget.value)
          }}
        />
      </Fieldset>
    </>
  )
}
